import { TrashIcon } from '@heroicons/react/24/outline';
import { Handle, Position, type NodeProps } from '@xyflow/react';

import AudioEditForm from '../../forms/AudioEditForm';
import AudioGenerationForm from '../../forms/AudioGenerationForm';
import ImageGenerationForm from '../../forms/ImageGenerationForm';
import LipSyncForm from '../../forms/LipSyncForm';
import VideoGenerationForm from '../../forms/VideoGenerationForm';
import { useClipCanvasContext } from '../ClipCanvasContext';
import type { ClipCanvasNode, FormGeneratorNodeData } from '../types';

type FormGeneratorNodeProps = NodeProps<ClipCanvasNode>;

function acceptsImageReference(formType: FormGeneratorNodeData['formType']): boolean {
  return formType === 'start-image'
    || formType === 'end-image'
    || formType === 'storyboard-image'
    || formType === 'video';
}

function acceptsAudioReference(formType: FormGeneratorNodeData['formType']): boolean {
  return formType === 'audio'
    || formType === 'audio-edit'
    || formType === 'video'
    || formType === 'lip-sync';
}

export default function FormGeneratorNode({ id, data }: FormGeneratorNodeProps) {
  const ctx = useClipCanvasContext();
  if (data.kind !== 'form-generator') return null;

  const injectEvent = ctx.injectEvents[id];
  const imageInjectEvent = !injectEvent || injectEvent.mediaType === 'image' ? injectEvent : undefined;
  const audioInjectEvent = injectEvent?.mediaType === 'audio' ? injectEvent : undefined;
  const formData = data as FormGeneratorNodeData;
  const handleReferenceImageCleared = (url: string) => {
    formData.actions?.onReferenceMediaCleared?.(id, url, 'image');
  };
  const handleReferenceAudioCleared = (url: string) => {
    formData.actions?.onReferenceMediaCleared?.(id, url, 'audio');
  };
  const handleCanvasSubmitSuccess = () => {
    void formData.actions?.onRemoveFormNode?.(id);
  };

  const renderForm = () => {
    if (
      formData.formType === 'start-image'
      || formData.formType === 'end-image'
      || formData.formType === 'storyboard-image'
    ) {
      const frameType = formData.formType === 'start-image'
        ? 'start'
        : formData.formType === 'end-image'
          ? 'end'
          : 'storyboard';

      return (
        <ImageGenerationForm
          videoClip={ctx.videoClip}
          models={ctx.models}
          modelsLoading={ctx.modelsLoading}
          refreshVideoClip={ctx.refreshVideoClip}
          editConfig={ctx.editConfig}
          resources={ctx.resources}
          isPolling={ctx.isPolling}
          onTriggerPoll={ctx.onTriggerResourcePoll}
          episode={ctx.episode}
          frameType={frameType}
          externalAddImage={imageInjectEvent ? { url: imageInjectEvent.url, nonce: imageInjectEvent.nonce } : undefined}
          onReferenceImageCleared={handleReferenceImageCleared}
          onCanvasSubmitSuccess={handleCanvasSubmitSuccess}
          initialParams={formData.initialParams}
          canvasMode
        />
      );
    }

    if (formData.formType === 'audio') {
      return (
        <AudioGenerationForm
          videoClip={ctx.videoClip}
          models={ctx.models}
          modelsLoading={ctx.modelsLoading}
          refreshVideoClip={ctx.refreshVideoClip}
          resources={ctx.resources}
          isPolling={ctx.isPolling}
          onTriggerPoll={ctx.onTriggerResourcePoll}
          canvasMode
          canvasActiveTab="tts"
          externalAddAudio={audioInjectEvent ? { url: audioInjectEvent.url, nonce: audioInjectEvent.nonce } : undefined}
          onReferenceAudioCleared={handleReferenceAudioCleared}
          onCanvasSubmitSuccess={handleCanvasSubmitSuccess}
          initialParams={formData.initialParams}
        />
      );
    }

    if (formData.formType === 'audio-edit') {
      return (
        <AudioEditForm
          videoClip={ctx.videoClip}
          models={ctx.models}
          modelsLoading={ctx.modelsLoading}
          refreshVideoClip={ctx.refreshVideoClip}
          resources={ctx.resources}
          onTriggerPoll={ctx.onTriggerResourcePoll}
          canvasMode
          externalAddAudio={audioInjectEvent ? { url: audioInjectEvent.url, nonce: audioInjectEvent.nonce } : undefined}
          onReferenceAudioCleared={handleReferenceAudioCleared}
          onCanvasSubmitSuccess={handleCanvasSubmitSuccess}
          initialParams={formData.initialParams}
        />
      );
    }

    if (formData.formType === 'lip-sync') {
      return (
        <LipSyncForm
          videoClip={ctx.videoClip}
          models={ctx.models}
          modelsLoading={ctx.modelsLoading}
          refreshVideoClip={ctx.refreshVideoClip}
          resources={ctx.resources}
          isPolling={ctx.isPolling}
          onTriggerPoll={ctx.onTriggerResourcePoll}
          canvasMode
          externalAddAudio={audioInjectEvent ? { url: audioInjectEvent.url, nonce: audioInjectEvent.nonce } : undefined}
          onReferenceAudioCleared={handleReferenceAudioCleared}
          onCanvasSubmitSuccess={handleCanvasSubmitSuccess}
          initialParams={formData.initialParams}
        />
      );
    }

    return (
      <VideoGenerationForm
        videoClip={ctx.videoClip}
        models={ctx.models}
        modelsLoading={ctx.modelsLoading}
        refreshVideoClip={ctx.refreshVideoClip}
        editConfig={ctx.editConfig}
        onEditConfigChange={ctx.onEditConfigChange}
        resources={ctx.resources}
        isPolling={ctx.isPolling}
        onTriggerPoll={ctx.onTriggerResourcePoll}
        episode={ctx.episode}
        externalAddImage={imageInjectEvent ? { url: imageInjectEvent.url, nonce: imageInjectEvent.nonce } : undefined}
        externalAddAudio={audioInjectEvent ? { url: audioInjectEvent.url, nonce: audioInjectEvent.nonce } : undefined}
        onReferenceImageCleared={handleReferenceImageCleared}
        onReferenceAudioCleared={handleReferenceAudioCleared}
        onCanvasSubmitSuccess={handleCanvasSubmitSuccess}
        initialParams={formData.initialParams}
        canvasMode
        canvasActiveTab="video"
      />
    );
  };

  const acceptsImage = acceptsImageReference(formData.formType);
  const acceptsAudio = acceptsAudioReference(formData.formType);
  const hasTwoInputs = acceptsImage && acceptsAudio;

  return (
    <div className="clip-canvas-generator clip-canvas-generator--form nowheel">
      {acceptsImage && (
        <Handle
          id="reference:image"
          type="target"
          position={Position.Left}
          className={`clip-canvas-generator__handle clip-canvas-generator__handle--image ${hasTwoInputs ? '' : 'clip-canvas-generator__handle--single'}`}
          title="图片输入"
        />
      )}
      {acceptsAudio && (
        <Handle
          id="reference:audio"
          type="target"
          position={Position.Left}
          className={`clip-canvas-generator__handle clip-canvas-generator__handle--audio ${hasTwoInputs ? '' : 'clip-canvas-generator__handle--single'}`}
          title="音频输入"
        />
      )}
      <div className="clip-canvas-generator__shell">
        <div className="clip-canvas-generator__header drag-handle">
          <span className="clip-canvas-generator__title">{formData.title}</span>
          <button
            type="button"
            className="clip-canvas-generator__delete nodrag nopan"
            onClick={(event) => {
              event.stopPropagation();
              void formData.actions?.onDeleteFormNode?.(id);
            }}
            aria-label={`删除${formData.title}`}
            title={`删除${formData.title}`}
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="clip-canvas-generator__body nodrag nopan nowheel">{renderForm()}</div>
      </div>
    </div>
  );
}
